/**
 * Skeleton Component
 * Loading placeholder with design system tokens and customizable shapes
 */

import React from "react";
import {
  Skeleton as ChakraSkeleton,
  SkeletonText as ChakraSkeletonText,
  SkeletonCircle as ChakraSkeletonCircle,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import { SurfaceColorToken, PrimitiveColorToken } from "design-system/foundation/Color";
import CDS from "design-system";

// Branded types for Skeleton variants and sizes
declare const __skeleton_variant_brand: unique symbol;
declare const __skeleton_size_brand: unique symbol;

export interface SkeletonVariantToken {
  readonly [__skeleton_variant_brand]: true;
  readonly _value: string;
}

export interface SkeletonSizeToken {
  readonly [__skeleton_size_brand]: true;
  readonly _value: string;
}

// Factory functions
const createSkeletonVariantToken = (value: string): SkeletonVariantToken => ({ _value: value } as SkeletonVariantToken);

const createSkeletonSizeToken = (value: string): SkeletonSizeToken => ({ _value: value } as SkeletonSizeToken);

export interface SkeletonProps {
  variant?: SkeletonVariantToken;
  size?: SkeletonSizeToken;
  startColor?: SurfaceColorToken | PrimitiveColorToken;
  endColor?: SurfaceColorToken | PrimitiveColorToken;
  lines?: number; // For text skeleton
  spacing?: string; // For text skeleton line spacing (already resolved)
  children?: React.ReactNode;
  // Common React props
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  // Accessibility
  "aria-label"?: string;
  role?: string;
}

function SkeletonComponent({
  variant = skeletonTokens.variant.rectangle,
  size = skeletonTokens.size.medium,
  startColor,
  endColor,
  lines = 3,
  spacing = "4px",
  children,
  ...props
}: SkeletonProps) {
  // Better default colors for skeleton visibility with light/dark mode support
  const defaultStartColor = useColorModeValue(
    startColor || CDS.Color.primitive.gray(200),
    startColor || CDS.Color.primitive.gray(700),
  );
  const defaultEndColor = useColorModeValue(
    endColor || CDS.Color.primitive.gray(300),
    endColor || CDS.Color.primitive.gray(600),
  );

  // Final resolved colors
  const resolvedStartColor = defaultStartColor;
  const resolvedEndColor = defaultEndColor;

  const getSizeStyles = (size: SkeletonSizeToken, variant: SkeletonVariantToken) => {
    const baseStyles = {
      startColor: resolvedStartColor,
      endColor: resolvedEndColor,
    };

    switch (variant._value) {
      case "circle":
        switch (size._value) {
          case "small":
            return { ...baseStyles, size: "32px" };
          case "large":
            return { ...baseStyles, size: "64px" };
          case "medium":
          default:
            return { ...baseStyles, size: "48px" };
        }
      case "text":
        switch (size._value) {
          case "small":
            return { ...baseStyles, fontSize: "sm", lineHeight: "4" };
          case "large":
            return { ...baseStyles, fontSize: "lg", lineHeight: "7" };
          case "medium":
          default:
            return { ...baseStyles, fontSize: "md", lineHeight: "5" };
        }
      case "rectangle":
      default:
        switch (size._value) {
          case "small":
            return {
              ...baseStyles,
              height: "24px",
              borderRadius: "4px",
            };
          case "large":
            return {
              ...baseStyles,
              height: "64px",
              borderRadius: "8px",
            };
          case "medium":
          default:
            return {
              ...baseStyles,
              height: "40px",
              borderRadius: "8px",
            };
        }
    }
  };

  const renderVariant = (variant: SkeletonVariantToken) => {
    const sizeStyles = getSizeStyles(size, variant);

    switch (variant._value) {
      case "circle":
        return (
          <ChakraSkeletonCircle
            {...sizeStyles}
            {...props}
          />
        );
      case "text":
        return (
          <ChakraSkeletonText
            noOfLines={lines}
            spacing={spacing}
            skeletonHeight={sizeStyles.fontSize === "sm" ? "3" : sizeStyles.fontSize === "lg" ? "5" : "4"}
            {...sizeStyles}
            {...props}
          />
        );
      case "avatar":
        return (
          <HStack spacing={4}>
            <ChakraSkeletonCircle
              size="40px"
              {...sizeStyles}
            />
            <VStack align="start" spacing={1}>
              <ChakraSkeleton
                height="16px"
                width="120px"
                {...sizeStyles}
              />
              <ChakraSkeleton
                height="12px"
                width="80px"
                {...sizeStyles}
              />
            </VStack>
          </HStack>
        );
      case "card":
        return (
          <VStack align="stretch" spacing={3} w="full">
            <ChakraSkeleton
              height="128px"
              width="full"
              borderRadius="8px"
              {...sizeStyles}
            />
            <VStack align="stretch" spacing={1}>
              <ChakraSkeleton
                height="20px"
                width="70%"
                {...sizeStyles}
              />
              <ChakraSkeleton
                height="16px"
                width="90%"
                {...sizeStyles}
              />
              <ChakraSkeleton
                height="16px"
                width="60%"
                {...sizeStyles}
              />
            </VStack>
          </VStack>
        );
      case "rectangle":
      default:
        return (
          <ChakraSkeleton
            {...sizeStyles}
            {...props}
          >
            {children}
          </ChakraSkeleton>
        );
    }
  };

  return renderVariant(variant);
}

// Skeleton tokens
const skeletonTokens = {
  variant: {
    rectangle: createSkeletonVariantToken("rectangle"),
    circle: createSkeletonVariantToken("circle"),
    text: createSkeletonVariantToken("text"),
    avatar: createSkeletonVariantToken("avatar"),
    card: createSkeletonVariantToken("card"),
  } as const,

  size: {
    small: createSkeletonSizeToken("small"),
    medium: createSkeletonSizeToken("medium"),
    large: createSkeletonSizeToken("large"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Skeleton = Object.assign(SkeletonComponent, skeletonTokens);
